<template>
    <div class="p-3">
        <div class="mb-3">
            <label class="form-label fw-semibold text-xs">
                <i class="fas fa-search text-secondary me-1"></i>
                Buscar inmobiliaria (usuarios con rol admin)
            </label>
            <div class="d-flex gap-2">
                <input
                    v-model="query"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Nombre o correo (min. 2 caracteres)..."
                    @keyup.enter="doSearch"
                />
                <soft-button
                    variant="gradient"
                    color="primary"
                    size="sm"
                    @click="doSearch"
                    :loading="searching"
                    :disabled="!canSearch"
                >
                    <i class="fas fa-search me-1"></i>
                    Buscar
                </soft-button>
            </div>
            <small class="text-muted text-xxs d-block mt-1">
                Presiona <kbd>Enter</kbd> o el botón <strong>Buscar</strong>. Solo usuarios
                <code>is_main = true</code> que no pertenezcan ya a esta lonja.
            </small>
        </div>

        <div v-if="errorMsg" class="alert alert-danger py-2 px-3 text-sm">
            {{ errorMsg }}
        </div>

        <div class="results-box">
            <div v-if="searching" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-primary" role="status">
                    <span class="visually-hidden">Buscando...</span>
                </div>
                <span class="text-muted ms-2 small">Buscando...</span>
            </div>

            <div v-else-if="hasSearched && !filteredResults.length" class="text-center text-muted py-4 small">
                <i class="fas fa-inbox d-block mb-2" style="font-size: 1.5rem; opacity: 0.5;"></i>
                No se encontraron inmobiliarias disponibles
            </div>

            <div v-else-if="!hasSearched" class="text-center text-muted py-4 small">
                <i class="fas fa-search d-block mb-2" style="font-size: 1.5rem; opacity: 0.5;"></i>
                Escribe un término y presiona Buscar
            </div>

            <ul v-else class="list-unstyled mb-0 result-list">
                <li
                    v-for="user in filteredResults"
                    :key="user.id"
                    class="result-item d-flex align-items-center justify-content-between"
                >
                    <div class="d-flex align-items-center gap-2">
                        <div class="avatar-small">
                            <img
                                v-if="userLogo(user)"
                                :src="userLogo(user)"
                                alt="Logo"
                                @error="onImgError"
                            />
                            <i v-else class="fas fa-building text-secondary"></i>
                        </div>
                        <div>
                            <div class="text-sm fw-semibold text-dark">
                                {{ userDisplayName(user) }}
                            </div>
                            <div class="text-xs text-muted">{{ user.email }}</div>
                        </div>
                    </div>
                    <soft-button
                        variant="gradient"
                        color="primary"
                        size="sm"
                        @click="pickUser(user)"
                        :loading="addingId === user.id"
                    >
                        <i class="fas fa-plus me-1"></i>
                        Agregar
                    </soft-button>
                </li>
            </ul>
        </div>

        <div class="d-flex justify-content-end mt-3 pt-3 border-top">
            <soft-button variant="outline" color="secondary" size="sm" @click="$emit('close')">
                Cerrar
            </soft-button>
        </div>
    </div>
</template>

<script>
import { ref, computed } from 'vue';
import SoftButton from '@/components/SoftButton.vue';
import { useLonja } from '../useLonja.js';

export default {
    name: 'AddMemberModal',
    components: { SoftButton },
    props: {
        lonjaId: { type: [Number, String], required: true },
        existingMemberIds: { type: Array, default: () => [] }
    },
    emits: ['close', 'added'],
    setup(props, { emit }) {
        const { searchMainUsers, addMember } = useLonja();

        const query = ref('');
        const results = ref([]);
        const searching = ref(false);
        const hasSearched = ref(false);
        const addingId = ref(null);
        const errorMsg = ref(null);

        const canSearch = computed(
            () => !!query.value && query.value.trim().length >= 2 && !searching.value
        );

        const doSearch = async () => {
            if (!canSearch.value) {
                errorMsg.value = 'Ingresa al menos 2 caracteres para buscar';
                return;
            }
            searching.value = true;
            errorMsg.value = null;
            try {
                const data = await searchMainUsers(query.value.trim(), 1);
                results.value = Array.isArray(data) ? data : data?.data || [];
                hasSearched.value = true;
            } catch (err) {
                errorMsg.value = 'Error al buscar usuarios';
            } finally {
                searching.value = false;
            }
        };

        const isMainUser = (user) => {
            if (user.is_main === true || user.is_main === 1) return true;
            if (Array.isArray(user.roles)) {
                return user.roles.some((r) => r.is_main === true || r.is_main === 1);
            }
            if (user.roles_users?.role) {
                return !!user.roles_users.role.is_main;
            }
            return false;
        };

        const filteredResults = computed(() => {
            const existing = new Set(props.existingMemberIds);
            return results.value.filter((u) => isMainUser(u) && !existing.has(u.id));
        });

        const userDisplayName = (user) => {
            if (user.userdata?.primer_nombre) {
                const nombre = user.userdata.primer_nombre;
                const apellido = user.userdata.primer_apellido || '';
                return `${nombre} ${apellido}`.trim();
            }
            return (
                user.empresa?.nombre ||
                user.username ||
                user.name ||
                user.email ||
                'Sin nombre'
            );
        };

        const userLogo = (user) => {
            return user.empresa?.logo || user.userdata?.foto || null;
        };

        const onImgError = (e) => {
            e.target.style.display = 'none';
        };

        const pickUser = async (user) => {
            addingId.value = user.id;
            errorMsg.value = null;
            try {
                const result = await addMember(props.lonjaId, user.id);
                if (result.success) {
                    results.value = results.value.filter((u) => u.id !== user.id);
                    emit('added', user);
                } else if (result.errors) {
                    const firstKey = Object.keys(result.errors)[0];
                    errorMsg.value =
                        result.errors[firstKey]?.[0] || 'No se pudo agregar la inmobiliaria';
                }
            } finally {
                addingId.value = null;
            }
        };

        return {
            query,
            results,
            searching,
            hasSearched,
            addingId,
            errorMsg,
            filteredResults,
            canSearch,
            doSearch,
            userDisplayName,
            userLogo,
            onImgError,
            pickUser
        };
    }
};
</script>

<style lang="scss" scoped>
.results-box {
    max-height: 360px;
    overflow-y: auto;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    background: #fff;
}

.result-list {
    padding: 0.25rem 0;
}

.result-item {
    padding: 0.6rem 0.9rem;
    border-bottom: 1px solid #f0f2f5;
    transition: background 0.15s ease;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: #f8f9fa;
    }
}

.avatar-small {
    width: 36px;
    height: 36px;
    border-radius: 0.5rem;
    background: #f0f2f5;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.form-control:focus {
    border-color: #ef9a9a;
    box-shadow: 0 0 0 2px rgba(229, 57, 53, 0.12);
}
</style>
